import * as React from "react";
import jsYaml from "js-yaml";
import { ViewModal } from "../../../components/modals/viewModal";
import { linksOf, request } from "../../../services/cpln";
import { Button, Checkbox, Dropdown, notification, Tag } from "antd";
import { k8sKeySort, toSortedJSON } from "../../../services/utils";
import clsx from "clsx";
import "./jobExecutions.scss";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CommandModal_StartCronJob } from "../commands/commandModal_startCronJob";
import { useBasePath } from "../../../reactContexts/basePathContext";
import { CommandModal_ConfirmReplicaTermination } from "../commands/commandModal_confirmReplicaTermination";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { DateString } from "../../../components/dateString";
import { formatStrings } from "../../../utils/time";
import { subSeconds, addSeconds, addWeeks, differenceInDays } from "date-fns";
import { Deployment } from "../../../schema/types/workload/deployment";
import { Message } from "../statefulDeployments/message";
import { RunCronWorkloadStatus } from "../../../schema/types/command";
import { useInitCommandsContext } from "../../../components/detail/command/commandsContext";
import { Workload } from "../../../schema/types/workload/entity";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { useAppSelector } from "../../../store/hooks";
import { selectOrg } from "../../../store/contextSlice";
import { selectLabel } from "../../../store/timezoneSlice";
import { ChevronDown } from "react-feather";

const STORAGE_KEY_VIEW_CONTAINERS = "workload-job-view-containers";

interface ConfirmReplicaTerminationProps {
  location: string;
  replica: string;
}
interface Props {
  workload: Workload;
  deployments: Deployment[];
}

// TODO update icons
export const JobExecutions: React.FC<Props> = ({ workload, deployments: deploymentItems }) => {
  const org = useAppSelector(selectOrg);
  const timezoneLabel = useAppSelector(selectLabel);
  const basePath = useBasePath("/gvc/:gvc/workload/:workload/*");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { commands, startFetchCommandsInterval, fetchCommandsPage } = useInitCommandsContext(linksOf(workload).self!);
  React.useEffect(() => {
    startFetchCommandsInterval();
  }, []);

  const deployments = React.useMemo<Deployment[]>(() => {
    const result = [...deploymentItems];
    const deploymentLocations: string[] = deploymentItems.map((d) => d.name).filter(Boolean);

    for (const c of commands.toReversed()) {
      if (
        //
        !!c.spec?.location &&
        !deploymentLocations.includes(c.spec.location) &&
        c.type === "runCronWorkload" && //
        !!c.lifecycleStage &&
        ["running", "pending", "failed"].includes(c.lifecycleStage)
      ) {
        result.push({
          name: c.spec.location,
          kind: "deployment",
          links: [{ href: "self", rel: `${linksOf(workload).self}/deployment/${c.spec.location}` }],
        });
        deploymentLocations.push(c.spec.location);
      }
    }
    return result;
  }, [deploymentItems, deploymentItems, commands, commands]);

  const updatedTimeoutIdRef = React.useRef<any>(null);
  const [collapsedDeployments, setCollapsedDeployments] = React.useState<string[]>([]);
  const [viewingData, setViewingData] = React.useState<{ jobExecutions: any[]; commands: any[] } | null>(null);
  function getDefaultViewContainers() {
    let value = false;
    const localValue = window.localStorage.getItem(STORAGE_KEY_VIEW_CONTAINERS);
    if (localValue && localValue === "true") {
      value = true;
    }
    return value;
  }
  const [viewContainers, setViewContainers] = React.useState(getDefaultViewContainers());
  const [startCronJobLocation, setStartCronJobLocation] = React.useState("");
  const [terminateReplicaModal, setTerminateReplicaModal] = React.useState<ConfirmReplicaTerminationProps | undefined>(
    undefined,
  );

  React.useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY_VIEW_CONTAINERS, String(viewContainers));
  }, [viewContainers]);

  React.useEffect(() => {
    return () => {
      if (updatedTimeoutIdRef.current) {
        clearTimeout(updatedTimeoutIdRef.current);
      }
    };
  }, []);

  function onToggleCollapseDeployment(deployment: string) {
    let _collapsedDeployments = [...collapsedDeployments];
    if (_collapsedDeployments.includes(deployment)) {
      _collapsedDeployments = _collapsedDeployments.filter((x) => x !== deployment);
    } else {
      _collapsedDeployments.push(deployment);
    }
    setCollapsedDeployments(_collapsedDeployments);
  }

  async function handleExportMenuClick(e: any) {
    if (e.key === "json") {
      await exportJSON();
    } else if (e.key === "yaml") {
      await exportYaml();
    }
  }

  async function exportJSON() {
    try {
      const a = document.createElement("a");
      a.style.display = "none";
      a.classList.add("cpln-temp-a");
      a.download = workload.name + "-deployments.json";
      let blob = "";
      const { data } = await request({ url: linksOf(workload).self! + "/deployment" });
      blob = JSON.stringify(toSortedJSON(data), null, 2);
      const file = new Blob([blob], { type: "text/json" });
      const href = URL.createObjectURL(file);
      a.href = href;
      a.click();
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  async function exportYaml() {
    try {
      const a = document.createElement("a");
      a.style.display = "none";
      a.classList.add("cpln-temp-a");
      a.download = workload.name + "-deployments.yaml";
      let blob = "";
      const { data } = await request({ url: linksOf(workload).self! + "/deployment" });
      blob = jsYaml.dump(data, { indent: 2, noRefs: true, sortKeys: k8sKeySort });
      const file = new Blob([blob], { type: "text/yaml" });
      const href = URL.createObjectURL(file);
      a.href = href;
      a.click();
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  async function fetchCombinedData() {
    try {
      const [deploymentsRes, commandsRes] = await Promise.all([
        request({ url: linksOf(workload).self! + "/deployment" }),
        request({ url: linksOf(workload).self! + "/-command" }),
      ]);
      const jobExecutions = deploymentsRes.data?.items || [];
      const commandItems = commandsRes.data?.items || [];
      setViewingData({ jobExecutions, commands: commandItems });
    } catch (e) {
      notification.warning({ message: "Failed to fetch data", description: e.message });
    }
  }

  const isCollapsedAll = deployments.length === collapsedDeployments.length;

  function onToggleCollapseAll() {
    if (isCollapsedAll) {
      setCollapsedDeployments([]);
    } else {
      setCollapsedDeployments(deployments.map((d) => d.name));
    }
  }

  return (
    <div>
      {viewingData && (
        <ViewModal
          object={viewingData}
          onClose={() => setViewingData(null)}
          title={workload.name + " Job Executions & Commands"}
          visible={!!viewingData}
          filename={workload.name + "-job-executions"}
          noSlim
        />
      )}
      {!!terminateReplicaModal ? (
        <CommandModal_ConfirmReplicaTermination
          workload={workload}
          location={terminateReplicaModal.location}
          replica={terminateReplicaModal.replica}
          onClose={() => setTerminateReplicaModal(undefined)}
          onDone={() => setTerminateReplicaModal(undefined)}
          visible={!!terminateReplicaModal}
        />
      ) : null}
      <div className="flex items-center mb-4">
        <Checkbox
          id={"view-containers"}
          className="mr-2"
          checked={viewContainers}
          onChange={(e) => setViewContainers(e.target.checked)}
        />
        <label className="cursor-pointer" htmlFor={"view-containers"}>
          View Containers
        </label>
      </div>
      <div className="w-full">
        {deployments
          .slice()
          .sort((a, b) => {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
          })
          .map((deployment, _deploymentIndex) => {
            let isCollapsed = collapsedDeployments.includes(deployment.name);

            const deploymentExecutions = (deployment.status?.jobExecutions as any) || [];
            const executions: any[] = [
              ...deploymentExecutions.map((de) => {
                const result = { ...de };
                const commandId = de.name.startsWith("command-") ? de.name.split("command-")[1] : undefined;
                const command = commandId ? commands.find((c) => c.id === commandId) : undefined;
                result.triggerType = commandId && !command ? null : commandId && command ? (!command.tags?.["cpln/owner"] ? "on-demand" : "scheduled") : "scheduled";
                if (command) {
                  result.tagName = command.tags?.name || "";
                }
                return result;
              }),
              ...commands
                .filter(
                  (c) =>
                    c.type === "runCronWorkload" &&
                    c.spec?.location === deployment.name &&
                    !!c.lifecycleStage &&
                    ["running", "pending", "failed"].includes(c.lifecycleStage) &&
                    !deploymentExecutions.some((e) => e.name === `command-${c.id}`),
                )
                .slice(0, 30)
                .map((c) => {
                  const messages = (c.status as RunCronWorkloadStatus).messages || [];
                  const containers: any = {};
                  for (const messageIndexStr in messages) {
                    const messageIndex = Number(messageIndexStr);
                    const message = messages[messageIndex];
                    containers[`cpln-ui-${messageIndexStr}`] = {
                      message: message,
                    };
                  }

                  return {
                    name: `command-${c.id}`,
                    tagName: c.tags?.name || "",
                    triggerType: c.tags?.["cpln/owner"] ? "scheduled" : "on-demand",
                    conditions: [],
                    containers: containers,
                    replica: c.status?.replica || "",
                    startTime: c.created,
                    workloadVersion: 0,
                    status: c.lifecycleStage === "running" ? "active" : c.lifecycleStage,
                  };
                }),
            ];
            return (
              <div className={"mb-10"} key={deployment.name}>
                <div className={`flex items-center text-xl font-medium mb-4 gap-2 w-full`}>
                  <span>{deployment.name}</span>
                  <button
                    type={"button"}
                    onClick={() => onToggleCollapseDeployment(deployment.name)}
                    className="text-base"
                  >
                    {isCollapsed ? (
                      <DownOutlined className={`feather-icon`} />
                    ) : (
                      <UpOutlined className={`feather-icon`} />
                    )}
                  </button>
                  <Button
                    type={"default"}
                    onClick={() => {
                      setStartCronJobLocation(deployment.name);
                    }}
                  >
                    Start Cron Job
                  </Button>
                  {_deploymentIndex === 0 && (
                    <>
                      <div className="flex-grow" />
                      <Button
                        type="text"
                        onClick={onToggleCollapseAll}
                        iconPosition="end"
                        icon={isCollapsedAll ? <DownOutlined /> : <UpOutlined />}
                      >
                        {isCollapsedAll ? "Expand All" : "Collapse All"}
                      </Button>
                      <Dropdown.Button
                        className="w-auto"
                        onClick={fetchCombinedData}
                        menu={{
                          onClick: handleExportMenuClick,
                          items: [
                            { key: "json", label: "Export JSON" },
                            { key: "yaml", label: "Export YAML" },
                          ],
                        }}
                      >
                        View Executions
                      </Dropdown.Button>
                    </>
                  )}
                </div>
                <Message
                  className="mb-2"
                  messages={[
                    deployment.status?.message || "",
                    ...(deployment.status?.versions || []).map((v) => v.message || ""),
                  ]}
                  logsLink={pathname.replace("-jobexecutions", "logs?execute=true")}
                  eventsLink={pathname.replace("-jobexecutions", "events")}
                />
                {isCollapsed ? null : (
                  <>
                    {executions.length < 1 ? (
                      <div>No job executions on this location</div>
                    ) : (
                      <div className="flex items-center px-4 py-2 table-labels rounded text-sm">
                        <div style={{ width: "20%" }}>Name</div>
                        <div style={{ width: "12%" }}>Source</div>
                        <div style={{ width: "12%" }}>Status</div>
                        <div style={{ width: "22%" }}>Start Time ({timezoneLabel})</div>
                        <div style={{ width: "22%" }}>Completion Time ({timezoneLabel})</div>
                        <div style={{ width: "12%" }}>Actions</div>
                      </div>
                    )}
                    {executions
                      .slice()
                      .sort((a, b) => {
                        const aDate = new Date(a.startTime).getTime();
                        const bDate = new Date(b.startTime).getTime();
                        if (aDate < bDate) return 1;
                        if (aDate > bDate) return -1;
                        return 0;
                      })
                      .map((exec, index) => {
                        const lastItem = index === executions.length - 1;

                        const liveLogs = exec.status === "active" && !exec.completionTime;

                        // Subtract 1 second for from date buffer
                        const startDate = new Date(exec.startTime);
                        const fromDateString = subSeconds(startDate, 1).toISOString();

                        let toDateString = "";
                        if (exec.completionTime) {
                          // Add 1 second for to date buffer
                          const completionDate = new Date(exec.completionTime);
                          toDateString = addSeconds(completionDate, 1).toISOString();
                        }

                        const now = new Date();
                        if (!exec.completionTime && differenceInDays(now, startDate) > 7) {
                          // Add 1 second buffer to limited end date
                          const limitedCompletionDate = addSeconds(addWeeks(startDate, 1), 1);
                          toDateString = limitedCompletionDate.toISOString();
                        }

                        const { gvc: gvcName } = parseLink(linksOf(workload).self!, { useInputCtx: true });
                        // Times are UTC ISO strings, so set tz=UTC to ensure logs page displays in UTC
                        const logLink = `${basePath}/logs?query=${encodeURIComponent(
                          `{gvc="${gvcName}", location="${deployment.name}", workload="${workload.name}", replica="${exec.replica}"}`,
                        )}&from=${fromDateString}${
                          toDateString ? `&to=${toDateString}` : ""
                        }&tz=UTC&execute=true&live=${liveLogs ? "true" : "false"}`;

                        const isNotRunning =
                          !exec.replica ||
                          exec.status === "successful" ||
                          exec.status === "failed" ||
                          exec.status === "invalid" ||
                          exec.status === "removed";

                        const stopCommand = commands.find(
                          (c) =>
                            c.type === "stopReplica" &&
                            c.lifecycleStage === "running" &&
                            c.spec?.replica === exec.replica,
                        );

                        return (
                          <>
                            <div className={clsx("jobExecution px-4 py-2 flex flex-col", { "border-b": !lastItem })}>
                              <div className="flex items-center">
                                <div className={clsx("pr-2 font-semibold")} style={{ width: "20%" }}>
                                  {exec.tagName || exec.name}
                                </div>
                                <div style={{ width: "12%" }}>
                                  {exec.triggerType === null ? null : exec.triggerType === "on-demand" ? (
                                    <Tag color="blue">On-Demand</Tag>
                                  ) : (
                                    <Tag color="default">Cron Schedule</Tag>
                                  )}
                                </div>
                                <div className={clsx("font-semibold")} style={{ width: "12%" }}>
                                  <span
                                    className={clsx({
                                      statusActive: exec.status === "active",
                                      statusSuccessful: exec.status === "successful",
                                      statusFailed: exec.status === "failed",
                                      statusInvalid: exec.status === "invalid",
                                      statusRemoved: exec.status === "removed",
                                    })}
                                  >
                                    {stopCommand ? (
                                      "Stopping"
                                    ) : (
                                      <>
                                        {exec.status.slice(0, 1).toUpperCase()}
                                        {exec.status.slice(1)}
                                      </>
                                    )}
                                  </span>
                                </div>
                                <div style={{ width: "22%" }}>
                                  <DateString iso={exec.startTime} format={formatStrings.sec} />
                                </div>
                                <div style={{ width: "22%" }}>
                                  <DateString iso={exec.completionTime} format={formatStrings.sec} />
                                </div>
                                <div style={{ width: "12%" }}>
                                  <Dropdown
                                    trigger={["click"]}
                                    menu={{
                                      onClick: async function ({ key }: any) {
                                        switch (key) {
                                          case "connect":
                                            const connectLink = `${basePath}/connect?location=${deployment.name}&container=${workload.spec.containers[0].name}&replica=${exec.replica}&autoConnect=true`;
                                            navigate(connectLink);
                                            break;
                                          case "view-command":
                                            const commandLink = `${basePath}/commands/${
                                              exec.name.split("command-")[1]
                                            }?fromExecutions=true`;
                                            navigate(commandLink);
                                            break;
                                          case "view-logs":
                                            navigate(logLink);
                                            break;
                                          case "stop-replica":
                                            setTerminateReplicaModal({
                                              location: deployment.name,
                                              replica: exec.replica,
                                            });
                                            break;
                                          default:
                                            break;
                                        }
                                      },
                                      items: [
                                        {
                                          key: "connect",
                                          label: "Connect",
                                          disabled: isNotRunning,
                                        },
                                        {
                                          key: "stop-replica",
                                          label: "Stop Replica",
                                          danger: !isNotRunning,
                                          disabled: isNotRunning || stopCommand,
                                        },
                                        exec.name.startsWith("command-")
                                          ? {
                                              key: "view-command",
                                              label: "View Command",
                                            }
                                          : (undefined as any),
                                        {
                                          key: "view-logs",
                                          label: "View Logs",
                                          disabled: !exec.replica,
                                        },
                                      ].filter(Boolean),
                                    }}
                                  >
                                    <Button
                                      type={"primary"}
                                      icon={<ChevronDown size={16} />}
                                      iconPosition="end"
                                    >
                                      Actions
                                    </Button>
                                  </Dropdown>
                                </div>
                              </div>
                              {exec.message ? (
                                <div className="px-2 py-1 color-danger text-sm">{exec.message}</div>
                              ) : null}
                              {!viewContainers && Object.values(exec.containers).some((c: any) => !!c.message) ? (
                                <div>
                                  {Object.values(exec.containers)
                                    .filter((c: any) => !!c.message)
                                    .map((c: any) => (
                                      <div className="px-2 py-1 color-danger">{c.message}</div>
                                    ))}
                                </div>
                              ) : null}
                              {viewContainers &&
                              Object.keys(exec.containers).filter((k) => !k.startsWith(`cpln-ui-`)).length > 0 ? (
                                <div className="px-4 my-2">
                                  <div className="pl-2 font-semibold">Containers</div>
                                  <div
                                    className="flex items-center px-2 py-1 table-labels border border-b-0"
                                    style={{ borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
                                  >
                                    <div className="w-3/12">Name</div>
                                    <div className="w-6/12">Image</div>
                                    <div className="w-1/12">CPU</div>
                                    <div className="w-1/12">Memory</div>
                                    <div className="w-1/12">Replicas</div>
                                  </div>
                                  {((Object.values(exec.containers) as unknown) as any[]).map((container, index) => {
                                    if (!container.name) {
                                      return null;
                                    }

                                    return (
                                      <div className={clsx("border", { "border-t-0": index !== 0 })}>
                                        <div className={`flex items-center px-2 py-1`}>
                                          <div className="w-3/12">{container.name}</div>
                                          <div className="w-6/12 truncate">
                                            {container.image.startsWith(`/org/${org}/image/`) &&
                                            !container.image.includes("@sha256:") ? (
                                              <Link
                                                className={`ngfocus color-link`}
                                                to={`/console/org/${org}/image/${container.image
                                                  .replace(`/org/${org}/image/`, "")
                                                  .replaceAll(":", "/")}`}
                                              >
                                                {container.image}
                                              </Link>
                                            ) : (
                                              container.image
                                            )}
                                          </div>
                                          <div className="w-1/12">{container.resources.cpu}m</div>
                                          <div className="w-1/12">{container.resources.memory}Mi</div>
                                          <div className="w-1/12">
                                            {container.resources.replicasReady}/{container.resources.replicas}
                                          </div>
                                        </div>
                                        {container.message ? (
                                          <div className="px-2 py-1 color-danger">{container.message}</div>
                                        ) : null}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>
                          </>
                        );
                      })}
                  </>
                )}
              </div>
            );
          })}
      </div>
      {startCronJobLocation ? (
        <CommandModal_StartCronJob
          workload={workload}
          initialLocation={startCronJobLocation}
          onDone={() => {
            setStartCronJobLocation("");
            fetchCommandsPage(true);
          }}
          onClose={() => setStartCronJobLocation("")}
          visible={!!startCronJobLocation}
        />
      ) : null}
    </div>
  );
};
